import {useDroppable, useDraggable} from "@dnd-kit/core";
import {useDndMonitor} from "@dnd-kit/core";
import {MoneyLabel} from "../../../../../../components/general/MoneyLabel.tsx";
import {CheckIcon, EllipsisHorizontalCircleIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {DraggableAttachedItem} from "./DraggableAttachedItem.tsx";
import {DescriptionWithTooltip} from "./DescriptionWithTooltip.tsx";
import {AttachedItem, MatchStatus, DEFAULT_CURRENCY, Transaction, MatchAcceptData} from "../dndTypes.ts";
import {BillDocument} from "../types.ts";
import {BankAccountLogo} from "../../../../../../components/general/BankAccountLogo.tsx";
import {formatDate, canMatch, canMatchBillDirections, isFullyMatched, getSignedAmountFromDirection, getDocumentRelationName, canProcessCreditCardSettlement, formatTransactionBankAccountLabel, partitionMatchIds, billTypeLabel} from "../utils/utils.ts";
import {cleanDocumentFileName} from "../../../../../../lib/TFICLib.ts";
import {useState} from "react";
import {Client} from "../../../Client.ts";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";


interface MatchableCardProps {
    id: string;
    isMatched: boolean;
    matchId?: string | null;
    billId?: string | null;
    isDragging: boolean;
    attachedItems: AttachedItem[];
    onRemoveItem: (itemId: string) => void;
    status?: MatchStatus;
    onAccept: (data: MatchAcceptData) => void;
    onReject: () => void;
    activeDraggingId?: string | null;
    transaction?: Transaction;
    document?: BillDocument;
    activeTransaction?: Transaction | null;
    activeDocument?: BillDocument | null;
    isPlaceholder?: boolean;
    onDescriptionClick?: () => void;
    onAttachedDocumentClick?: (docId: string) => void;
    client?: Client | null;
    onBookTransactionAsPrivate?: (transactionId: string) => Promise<void>;
    onCreateBlacklistRule?: (transaction: Transaction, client: Client | null) => void;
    onWrongGLAClick?: (transaction: Transaction) => void;
    onMatchClick?: (docId: string) => void;
    onMarkBillAsPrivatelyPaid?: (documentId: string) => Promise<void>;
    onCreditCardSettlement?: (transaction: Transaction, businessId: string | null) => void;
    canProcessCreditCardStatements?: boolean;
    canBookVatOnTransaction?: boolean;
    showMatched?: boolean;
}

export function MatchableCard({
    id,
    isMatched,
    matchId,
    billId,
    isDragging,
    attachedItems,
    onRemoveItem,
    status = "pending",
    onAccept,
    onReject,
    activeDraggingId = null,
    transaction,
    document,
    activeTransaction = null,
    activeDocument = null,
    isPlaceholder = false,
    onDescriptionClick,
    onAttachedDocumentClick,
    client,
    onBookTransactionAsPrivate,
    onCreateBlacklistRule,
    onWrongGLAClick,
    onMatchClick,
    onMarkBillAsPrivatelyPaid,
    onCreditCardSettlement,
    canProcessCreditCardStatements = false,
    canBookVatOnTransaction = false,
    showMatched = false,
}: MatchableCardProps) {
    const isDoc = document !== undefined;

    const title = transaction?.relation_name ||
        (document ? getDocumentRelationName(document.document_direction, document.scanned_fields) : "N/A");

    const description = transaction?.desc ||
        (document ? cleanDocumentFileName(document.file_name) : "");

    const date = transaction?.booking_date ||
        document?.scanned_fields?.normalized_datetime || "";

    const documentAmount = document?.scanned_fields?.total_amount_payable || {signed_amount: 0, currency: DEFAULT_CURRENCY};
    const signedAmount = getSignedAmountFromDirection(documentAmount, document?.document_direction);
    const adjustedDocumentAmount = document ? {...documentAmount, signed_amount: signedAmount} : documentAmount;

    const amount = transaction?.total_amount_payable || adjustedDocumentAmount;
    const currency = transaction?.bank_account_currency || documentAmount.currency || DEFAULT_CURRENCY;

    const showBankDetails = !!transaction;
    const bankLogoUrl = transaction?.bank_logo_url;
    const bankOwnerName = transaction?.bank_account_owner_name;
    const bankAccountLabel = formatTransactionBankAccountLabel(transaction);
    const referenceIdentifier = document?.scanned_fields?.reference_identifier;

    const hostNrsAmount: number | null = transaction?.nrs_amount ?? document?.nrs_amount ?? null;

    const [isIncompatible, setIsIncompatible] = useState(false);
    const [blocksMixedDirections, setBlocksMixedDirections] = useState(false);
    const hasAttachedItems = attachedItems.length > 0;
    const fullyMatched = isFullyMatched(amount.signed_amount, attachedItems, hostNrsAmount);

    const isDroppableWhenMatched = isMatched && showMatched && !!matchId;

    function getMatchedItemIds(): string[] {
        return attachedItems.map(item => item.id);
    }

    const {isOver, setNodeRef: setDropRef} = useDroppable({
        id,
        disabled: fullyMatched || isPlaceholder || (isMatched && !isDroppableWhenMatched),
    });

    useDndMonitor({
        onDragOver: ({over}) => {
            if (over?.id !== id) {
                setIsIncompatible(false);
                setBlocksMixedDirections(false);
                return;
            }

            if (isMatched && !isDroppableWhenMatched) {
                setIsIncompatible(true);
                setBlocksMixedDirections(false);
                return;
            }

            let transactionCurrency: string | undefined = undefined;
            let documentCurrency: string | undefined = undefined;
            let draggedNrsAmount: number | null | undefined = undefined;
            let mixedDirections = false;

            if (activeTransaction) {
                transactionCurrency = activeTransaction.total_amount_payable.currency ?? undefined;
                documentCurrency = document?.scanned_fields?.total_amount_payable?.currency ?? undefined;
                draggedNrsAmount = activeTransaction.nrs_amount ?? null;
            } else if (activeDocument) {
                documentCurrency = activeDocument.scanned_fields?.total_amount_payable?.currency ?? undefined;
                transactionCurrency = transaction?.total_amount_payable?.currency ?? undefined;
                draggedNrsAmount = activeDocument.nrs_amount ?? null;
                mixedDirections = !canMatchBillDirections(
                    [document?.document_direction, ...attachedItems.filter((item) => item.kind === 'bill').map((item) => item.document_direction)],
                    activeDocument.document_direction,
                );
            }

            if (draggedNrsAmount !== undefined) {
                const compatible = canMatch(
                    transactionCurrency,
                    documentCurrency,
                    hostNrsAmount,
                    draggedNrsAmount,
                ) && !mixedDirections;
                setIsIncompatible(!compatible);
                setBlocksMixedDirections(mixedDirections);
            } else {
                setIsIncompatible(false);
                setBlocksMixedDirections(false);
            }
        },
        onDragEnd: () => {
            setIsIncompatible(false);
            setBlocksMixedDirections(false);
        },
    });

    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
    } = useDraggable({id, disabled: hasAttachedItems || isPlaceholder || isMatched || isDroppableWhenMatched});

    const getBorderClass = () => {
        if (!isOver && !fullyMatched) return "";
        if (isOver && isIncompatible) return "outline-2 outline-dashed outline-red-500 outline-offset-[-2px]";
        if (isOver && fullyMatched) return "outline-2 outline-dashed outline-slate-400 outline-offset-[-2px]";
        if (isOver) return "outline-2 outline-dashed outline-emerald-500 outline-offset-[-2px]";
        return "outline outline-2 outline-emerald-500 outline-offset-[-2px]";
    };

    const getBackgroundClass = () => {
        if (!isOver && !fullyMatched) return "";
        if (isOver && isIncompatible) return "bg-red-50";
        if (isOver && fullyMatched) return "bg-slate-100";
        return "bg-emerald-50";
    };

    const style = {
        opacity: isDragging ? 0.5 : status === "accepted" ? 0.6 : 1,
    };

    const nrsRunningRemainder = (() => {
        if (hostNrsAmount == null) return null;
        let s = 0;
        for (const item of attachedItems) {
            if (item.nrs_amount == null) return null;
            s += item.nrs_amount;
        }
        return hostNrsAmount + s;
    })();

    const cardClassName = [
        "p-3 rounded-md bg-white mb-2 transition",
        isPlaceholder
            ? "cursor-default bg-gray-100"
            : isMatched
                ? "cursor-pointer"
                : hasAttachedItems
                    ? "cursor-default"
                    : "cursor-grab active:cursor-grabbing hover:bg-gray-50",
        isDragging && "opacity-50",
        status === "accepted" && "bg-green-50 border-green-200",
        "border border-gray-200",
        getBorderClass(),
        getBackgroundClass(),
    ]
        .filter(Boolean)
        .join(" ");

    const handleAcceptClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const currentItemId = id;
        const matchedItemIds = getMatchedItemIds();
        const hostId = billId ?? id;
        const {billIds, transactionIds} = partitionMatchIds(hostId, !!billId, attachedItems);
        const onAcceptdata: MatchAcceptData = {currentItemId, matchedItemIds, billIds, transactionIds, targetMatchId: matchId ?? undefined};
        onAccept(onAcceptdata);
    };

    const handleCardClick = () => {
        if (isMatched) {
            let docId: string | null | undefined;
            if (isDoc) {
                docId = id;
            } else if (transaction) {
                if ('matched_document_id' in transaction) {
                    docId = transaction.matched_document_id;
                }
            }
            if (docId) {
                onMatchClick?.(docId);
            }
            return;
        }
        if (isDoc && !hasAttachedItems) {
            onDescriptionClick?.();
        }
    };

    const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
        e.stopPropagation();
    };

    if (isPlaceholder) {
        return (
            <div
                ref={(el) => {
                    setDragRef(el);
                    setDropRef(el);
                }}
                className="p-3 rounded-md bg-gray-100 mb-2 border border-gray-200 min-h-24"
            />
        );
    }

    return (
        <div
            ref={(el) => {
                setDragRef(el);
                setDropRef(el);
            }}
            style={style}
            {...attributes}
            {...(!hasAttachedItems ? listeners : {})}
            onClick={handleCardClick}
            className={`${cardClassName} relative flex group hover:bg-gray-50 transition flex-col gap-3`}
        >
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="text-base font-medium text-gray-900 truncate">
                            {title}
                        </div>
                        {isMatched && (
                                <div className='shrink-0 inline-flex items-center rounded-full px-3 font-bold bg-yellow-300 text-gray-900 ring-1 ring-inset ring-yellow-200 text-xs py-[0.1rem] mb-[3px]'>
                                    Match
                                </div>
                            )}
                            {isDoc && (() => {
                                const label = billTypeLabel(document?.document_direction, document?.nrs_amount);
                                return label ? (
                                    <div className='shrink-0 inline-flex items-center rounded-full px-2.5 py-[0.1rem] mb-[3px] text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200'>
                                        {label}
                                    </div>
                                ) : null;
                            })()}
                            </div>
                        <MoneyLabel
                            value={amount.signed_amount / 100}
                            colorAid={true}
                            currency={currency}
                            showCurrency={true}
                        />
                    </div>
                    <div className="mt-1">
                        <DescriptionWithTooltip
                            description={description}
                            onClick={isDoc ? () => onDescriptionClick?.() : undefined}
                            containerClassName="max-w-[25vw]"
                            textClassName={isDoc ? "text-gray-700" : "text-gray-600"}
                        />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatDate(date)}</span>
                        <div className="flex items-center gap-2">
                            {showBankDetails && (
                                <div className="flex items-center gap-2">
                                    <div
                                        className="group ml-3 flex items-center gap-2 border border-gray-200 rounded-md p-2.5">
                                        <BankAccountLogo
                                            logoUrl={bankLogoUrl}
                                            accountType={transaction?.bank_account_type}
                                            className="mr-1 size-4 shrink-0"
                                        />
                                        <div className="flex flex-col">
                                            <div className="truncate text-xs group-hover:hidden">
                                                {bankOwnerName}
                                            </div>
                                            <span className="hidden group-hover:block text-gray-500 truncate">
                                            {bankAccountLabel}
                                        </span>
                                    </div>
                                </div>
                                <Menu as="div"
                                    className="relative inline-block text-left"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {({ close }) => {
                                        return (
                                            <>
                                                <MenuButton
                                                    type="button"
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex px-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                                                >
                                                    <EllipsisHorizontalCircleIcon className="size-5 text-gray-500 hover:text-gray-700" />
                                                </MenuButton>
                                                <MenuItems
                                                    transition
                                                    anchor="bottom end"
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="z-10 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none [--anchor-gap:0.5rem] data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                                                >
                                                    <div
                                                        className="py-1"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MenuItem as="div">
                                                            {({ focus }) => (
                                                                <button
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        close();
                                                                        if (transaction && onCreateBlacklistRule) {
                                                                            onCreateBlacklistRule(transaction, client || null);
                                                                        }
                                                                    }}
                                                                    className={`${
                                                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                                    } group flex items-center gap-2 px-4 py-2 text-sm w-full text-left`}
                                                                >
                                                                    <span>Automatisering aanmaken</span>
                                                                </button>
                                                            )}
                                                        </MenuItem>
                                                        <MenuItem as="div">
                                                            {({ focus }) => (
                                                                <button
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={async (e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();

                                                                        if (!transaction?.transaction_id || !onBookTransactionAsPrivate) {
                                                                            return;
                                                                        }

                                                                        close();
                                                                        try {
                                                                            await onBookTransactionAsPrivate(transaction.transaction_id);
                                                                        } catch (error) {
                                                                            console.error('Error in mark as private handler:', error);
                                                                        }
                                                                    }}
                                                                    className={`${
                                                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                                    } group flex items-center gap-2 px-4 py-2 text-sm w-full text-left`}
                                                                >
                                                                    <span>Markeer als privé</span>
                                                                </button>
                                                            )}
                                                        </MenuItem>
                                                        <MenuItem as="div">
                                                            {({ focus }) => (
                                                                <button
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        close();
                                                                        if (transaction && onWrongGLAClick) {
                                                                            onWrongGLAClick(transaction);
                                                                        }
                                                                    }}
                                                                    className={`${
                                                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                                    } group flex items-center gap-2 px-4 py-2 text-sm w-full text-left`}
                                                                >
                                                                    <span>{canBookVatOnTransaction ? 'Wegboeken' : 'Boeken naar'}</span>
                                                                </button>
                                                            )}
                                                        </MenuItem>
                                                        {canProcessCreditCardStatements && canProcessCreditCardSettlement(transaction) && (
                                                            <MenuItem as="div">
                                                                {({ focus }) => (
                                                                    <button
                                                                        onPointerDown={(e) => e.stopPropagation()}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            close();
                                                                            if (transaction && onCreditCardSettlement) {
                                                                                onCreditCardSettlement(transaction, client?.id ?? null);
                                                                            }
                                                                        }}
                                                                        className={`${
                                                                            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                                        } group flex items-center gap-2 px-4 py-2 text-sm w-full text-left`}
                                                                    >
                                                                        <span>Creditcard settlement verwerken</span>
                                                                    </button>
                                                                )}
                                                            </MenuItem>
                                                        )}
                                                    </div>
                                                </MenuItems>
                                            </>
                                        );
                                    }}
                                </Menu>
                                    </div>

                            )}

                            {referenceIdentifier && (
                                <div className="group ml-3 border border-gray-200 rounded-md p-2.5">
                                    <div className="flex text-center items-center">
                                        <div className="truncate text-xs">
                                            {referenceIdentifier}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isDoc && !isMatched && (
                                <Menu
                                    as="div"
                                    className="relative inline-block text-left"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {({ close }) => {
                                        const handleMarkAsUnmatchableClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
                                            e.preventDefault();
                                            e.stopPropagation();

                                            if (!document?.document_id || !onMarkBillAsPrivatelyPaid) {
                                                return;
                                            }

                                            close();
                                            try {
                                                await onMarkBillAsPrivatelyPaid(document.document_id);
                                            } catch (error) {
                                                console.error('Error in mark as unmatchable handler:', error);
                                            }
                                        };

                                        return (
                                            <>
                                                <MenuButton
                                                    type="button"
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex px-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                                                >
                                                    <EllipsisHorizontalCircleIcon className="size-5 text-gray-500 hover:text-gray-700" />
                                                </MenuButton>
                                                <MenuItems
                                                    transition
                                                    anchor="bottom end"
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="z-10 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none [--anchor-gap:0.5rem] data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                                                >
                                                    <div
                                                        className="py-1"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MenuItem as="div">
                                                            {({ focus }) => (
                                                                <button
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={handleMarkAsUnmatchableClick}
                                                                    className={`${
                                                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                                    } group flex items-center gap-2 px-4 py-2 text-sm w-full text-left`}
                                                                >
                                                                    <span>Betaald van privé-rekening</span>
                                                                </button>
                                                            )}
                                                        </MenuItem>
                                                    </div>
                                                </MenuItems>
                                            </>
                                        );
                                    }}
                                </Menu>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isOver && blocksMixedDirections && (
                <p className="mt-2 text-sm text-red-700">Een verkoopfactuur en een inkoopfactuur kunnen niet in dezelfde match. Match verkoop met ontvangsten, inkoop met betalingen.</p>
            )}
            {hasAttachedItems && (
                <div className="mt-3 space-y-2 relative z-0">
                    {attachedItems.map((item) => (
                        <DraggableAttachedItem
                            key={item.id}
                            item={item}
                            currency={currency}
                            onRemove={() => onRemoveItem(item.id)}
                            isDragging={activeDraggingId === item.id}
                            onOpenDocument={
                                item.document_category && onAttachedDocumentClick
                                    ? onAttachedDocumentClick
                                    : undefined
                            }
                        />
                    ))}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                            {nrsRunningRemainder == null ? (
                                <span>Verschil</span>
                            ) : nrsRunningRemainder > 0 ? (
                                <span>Nog te ontvangen: <MoneyLabel value={nrsRunningRemainder / 100} currency={currency} showCurrency={true} /></span>
                            ) : nrsRunningRemainder < 0 ? (
                                <span>Nog te betalen: <MoneyLabel value={Math.abs(nrsRunningRemainder) / 100} currency={currency} showCurrency={true} /></span>
                            ) : (
                                <span>Volledig gematched</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-700">
                            <span>Wil je deze transacties en deze documenten gaan matchen?</span>
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-green-50 text-green-600 border border-green-200 transition-colors"
                                onClick={handleAcceptClick}
                                onPointerDown={stopPropagation}
                                onMouseDown={stopPropagation}
                                onMouseUp={stopPropagation}
                                aria-label="Accept match"
                            >
                                <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-red-50 text-red-600 border border-red-200 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onReject();
                                }}
                                onPointerDown={stopPropagation}
                                onMouseDown={stopPropagation}
                                onMouseUp={stopPropagation}
                                aria-label="Reject match"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
